/**
 * SUPER ADMIN DASHBOARD
 *
 * Overview of leBusiness ERP platform metrics
 * - Total companies, users, revenue
 * - Active licenses and trials
 * - Quick actions
 */

import { createFileRoute, redirect, Link } from '@tanstack/react-router';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Building2,
  Users,
  Key,
  CreditCard,
  TrendingUp,
  TrendingDown,
  ArrowRight,
  Boxes,
  FlaskConical,
  Globe,
  AlertTriangle,
  CheckCircle,
  Clock,
  Activity,
} from 'lucide-react';

export const Route = createFileRoute('/admin/dashboard')({
  beforeLoad: ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/login' });
    }
    const profile = context.auth.profile;
    if (!profile?.isSuperAdmin) {
      throw redirect({ to: '/dashboard', replace: true });
    }
  },
  component: AdminDashboardPage,
});

// Mock data - replace with real API calls
const stats = {
  totalCompanies: 1247,
  companiesGrowth: 12.5,
  totalUsers: 4891,
  usersGrowth: 8.3,
  activeLicenses: 892,
  licensesGrowth: 15.2,
  monthlyRevenue: 4520000, // DZD
  revenueGrowth: 22.1,
  trialCompanies: 156,
  expiringLicenses: 23,
  pendingPayments: 8,
};

const recentActivity = [
  { id: 1, type: 'signup', company: 'SARL Tech Solutions', time: '5 min ago' },
  { id: 2, type: 'upgrade', company: 'ETS El Baraka', plan: 'Professional', time: '15 min ago' },
  { id: 3, type: 'payment', company: 'SPA Import-Export', amount: '45,000 DZD', time: '1 hour ago' },
  { id: 4, type: 'trial_start', company: 'Pharmacie Centrale', time: '2 hours ago' },
  { id: 5, type: 'license_expired', company: 'Restaurant Dz', time: '3 hours ago' },
];

const quickActions = [
  { name: 'View Companies', href: '/admin/companies', icon: Building2, color: 'bg-blue-500' },
  { name: 'Manage Licenses', href: '/admin/licenses', icon: Key, color: 'bg-emerald-500' },
  { name: 'Apps & Features', href: '/admin/features', icon: Boxes, color: 'bg-violet-500' },
  { name: 'A/B Tests', href: '/admin/ab-tests', icon: FlaskConical, color: 'bg-amber-500' },
  { name: 'Geography', href: '/admin/geography', icon: Globe, color: 'bg-pink-500' },
  { name: 'Plans & Pricing', href: '/admin/plans', icon: CreditCard, color: 'bg-cyan-500' },
];

function StatCard({
  title,
  value,
  change,
  icon: Icon,
  href,
}: {
  title: string;
  value: string | number;
  change: number;
  icon: React.ElementType;
  href: string;
}) {
  const isPositive = change >= 0;

  return (
    <Link to={href}>
      <Card className="bg-slate-800/50 border-slate-700 hover:border-violet-500/50 transition-colors cursor-pointer">
        <CardContent className="p-6">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-slate-400">{title}</p>
              <p className="text-3xl font-bold text-white mt-2">{value.toLocaleString()}</p>
              <div className="flex items-center gap-1 mt-2">
                {isPositive ? (
                  <TrendingUp className="w-4 h-4 text-emerald-400" />
                ) : (
                  <TrendingDown className="w-4 h-4 text-red-400" />
                )}
                <span className={isPositive ? 'text-emerald-400' : 'text-red-400'}>
                  {isPositive ? '+' : ''}{change}%
                </span>
                <span className="text-slate-500 text-sm">vs last month</span>
              </div>
            </div>
            <div className="p-3 rounded-lg bg-violet-500/20">
              <Icon className="w-6 h-6 text-violet-400" />
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

function AdminDashboardPage() {
  return (
    <AdminLayout>
      <div className="space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-white">Dashboard</h1>
          <p className="text-slate-400 mt-2">
            Welcome to the leBusiness Admin Panel. Monitor and manage your ERP platform.
          </p>
        </div>

        {/* Alerts */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="bg-amber-500/10 border-amber-500/30">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-2 rounded-full bg-amber-500/20">
                <Clock className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="text-amber-400 font-medium">{stats.expiringLicenses} Expiring Soon</p>
                <p className="text-sm text-amber-400/70">Licenses expiring in 7 days</p>
              </div>
              <ArrowRight className="w-5 h-5 text-amber-400 ml-auto" />
            </CardContent>
          </Card>

          <Card className="bg-blue-500/10 border-blue-500/30">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-2 rounded-full bg-blue-500/20">
                <Activity className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-blue-400 font-medium">{stats.trialCompanies} Active Trials</p>
                <p className="text-sm text-blue-400/70">Companies in trial period</p>
              </div>
              <ArrowRight className="w-5 h-5 text-blue-400 ml-auto" />
            </CardContent>
          </Card>

          <Card className="bg-red-500/10 border-red-500/30">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-2 rounded-full bg-red-500/20">
                <AlertTriangle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <p className="text-red-400 font-medium">{stats.pendingPayments} Pending Payments</p>
                <p className="text-sm text-red-400/70">Require attention</p>
              </div>
              <ArrowRight className="w-5 h-5 text-red-400 ml-auto" />
            </CardContent>
          </Card>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Total Companies"
            value={stats.totalCompanies}
            change={stats.companiesGrowth}
            icon={Building2}
            href="/admin/companies"
          />
          <StatCard
            title="Total Users"
            value={stats.totalUsers}
            change={stats.usersGrowth}
            icon={Users}
            href="/admin/users"
          />
          <StatCard
            title="Active Licenses"
            value={stats.activeLicenses}
            change={stats.licensesGrowth}
            icon={Key}
            href="/admin/licenses"
          />
          <StatCard
            title="Monthly Revenue"
            value={`${(stats.monthlyRevenue / 1000).toFixed(0)}K DZD`}
            change={stats.revenueGrowth}
            icon={CreditCard}
            href="/admin/analytics"
          />
        </div>

        {/* Quick Actions & Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Quick Actions */}
          <Card className="lg:col-span-1 bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">Quick Actions</CardTitle>
              <CardDescription className="text-slate-400">
                Common admin tasks
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {quickActions.map((action) => (
                <Link key={action.href} to={action.href}>
                  <Button
                    variant="ghost"
                    className="w-full justify-start text-slate-300 hover:text-white hover:bg-slate-700"
                  >
                    <div className={`p-1.5 rounded ${action.color} mr-3`}>
                      <action.icon className="w-4 h-4 text-white" />
                    </div>
                    {action.name}
                    <ArrowRight className="w-4 h-4 ml-auto opacity-50" />
                  </Button>
                </Link>
              ))}
            </CardContent>
          </Card>

          {/* Recent Activity */}
          <Card className="lg:col-span-2 bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">Recent Activity</CardTitle>
              <CardDescription className="text-slate-400">
                Latest platform events
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentActivity.map((activity) => (
                  <div key={activity.id} className="flex items-center gap-4 p-3 rounded-lg bg-slate-700/30">
                    <div className={`p-2 rounded-full ${
                      activity.type === 'signup' ? 'bg-emerald-500/20' :
                      activity.type === 'upgrade' ? 'bg-violet-500/20' :
                      activity.type === 'payment' ? 'bg-blue-500/20' :
                      activity.type === 'trial_start' ? 'bg-amber-500/20' :
                      'bg-red-500/20'
                    }`}>
                      {activity.type === 'signup' ? <Building2 className="w-4 h-4 text-emerald-400" /> :
                       activity.type === 'upgrade' ? <TrendingUp className="w-4 h-4 text-violet-400" /> :
                       activity.type === 'payment' ? <CreditCard className="w-4 h-4 text-blue-400" /> :
                       activity.type === 'trial_start' ? <Clock className="w-4 h-4 text-amber-400" /> :
                       <AlertTriangle className="w-4 h-4 text-red-400" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white font-medium truncate">
                        {activity.company}
                      </p>
                      <p className="text-xs text-slate-400">
                        {activity.type === 'signup' ? 'New company registered' :
                         activity.type === 'upgrade' ? `Upgraded to ${activity.plan}` :
                         activity.type === 'payment' ? `Payment received: ${activity.amount}` :
                         activity.type === 'trial_start' ? 'Started free trial' :
                         'License expired'}
                      </p>
                    </div>
                    <span className="text-xs text-slate-500">{activity.time}</span>
                  </div>
                ))}
              </div>
              <Button variant="ghost" className="w-full mt-4 text-slate-400 hover:text-white">
                View All Activity
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
}

export default AdminDashboardPage;
